import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import { LogOut, Monitor, UserRound } from 'lucide-react';

import { useCurrentUser } from '@/shared/auth';
import { Avatar, AvatarFallback } from '@/shared/ui/avatar';
import { Button } from '@/shared/ui/button';
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
} from '@/shared/ui/drawer';
import { Separator } from '@/shared/ui/separator';
import { cn } from '@/shared/lib/utils';

import { FieldTopbar } from './field-topbar';
import { FieldBottomNav } from './field-bottom-nav';
import { FieldMenuDrawer } from './field-menu-drawer';
import { useTheme } from '../../theme';

interface FieldAppShellProps {
  children: React.ReactNode;
}

/**
 * Mobile-first shell for the field PWA: sticky topbar + scrollable content +
 * fixed bottom nav. The "More" menu and the account drawer both slide up from
 * the bottom (vaul).
 *
 * The account drawer's "My Profile" routes to the in-app Profile screen
 * (`/field/profile`) — NOT the desktop `/profile` route, which would 401 the
 * prototype's mock session and bounce to /login.
 */
export function FieldAppShell({ children }: FieldAppShellProps) {
  const navigate = useNavigate();
  const user = useCurrentUser();
  const { theme } = useTheme();
  const [menuOpen, setMenuOpen] = React.useState(false);
  const [profileOpen, setProfileOpen] = React.useState(false);
  const [isOnline, setIsOnline] = React.useState<boolean>(
    typeof navigator === 'undefined' ? true : navigator.onLine,
  );

  React.useEffect(() => {
    function handleOnline() {
      setIsOnline(true);
    }
    function handleOffline() {
      setIsOnline(false);
    }
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  const initials =
    `${user?.firstName?.[0] ?? ''}${user?.lastName?.[0] ?? ''}`.toUpperCase() || '?';
  const displayName = user
    ? `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim() ||
      (user.userName ?? 'Field worker')
    : 'Field worker';

  return (
    <div
      className={cn(
        'field-surface tw-flex tw-h-[100dvh] tw-w-full tw-flex-col tw-overflow-hidden tw-bg-background',
        // `dark` drives the hand-written token block (.dark{}); `tw-dark` is
        // what Tailwind's prefixed `dark:` utility variants key off
        // (:is(.tw-dark *)). Both are needed so tokens AND dark: utilities work.
        theme === 'dark' && 'dark tw-dark',
      )}
    >
      <FieldTopbar
        isOnline={isOnline}
        onMenuToggle={() => {
          setMenuOpen((v) => !v);
          setProfileOpen(false);
        }}
        onProfileOpen={() => {
          setProfileOpen(true);
          setMenuOpen(false);
        }}
      />

      <main
        className="tw-flex-1 tw-overflow-y-auto tw-overscroll-contain"
        style={{ paddingBottom: 'calc(env(safe-area-inset-bottom, 0) + 88px)' }}
      >
        {children}
      </main>

      <FieldMenuDrawer open={menuOpen} onOpenChange={setMenuOpen} />

      {/* Account drawer — slides up from the avatar tap */}
      <Drawer open={profileOpen} onOpenChange={setProfileOpen}>
        <DrawerContent
          className={cn('field-surface tw-pb-6', theme === 'dark' && 'dark tw-dark')}
        >
          <DrawerHeader className="tw-pb-2">
            <DrawerTitle className="tw-text-base">Account</DrawerTitle>
          </DrawerHeader>

          <div className="tw-flex tw-items-center tw-gap-3 tw-px-4 tw-pb-3">
            <Avatar className="tw-h-12 tw-w-12">
              <AvatarFallback className="tw-bg-gradient-to-br tw-from-primary tw-to-sky-400 tw-text-sm tw-font-bold tw-text-white">
                {initials}
              </AvatarFallback>
            </Avatar>
            <div className="tw-flex tw-flex-1 tw-flex-col tw-overflow-hidden">
              <p className="tw-truncate tw-text-sm tw-font-semibold">{displayName}</p>
              <p className="tw-truncate tw-text-xs tw-text-muted-foreground">
                {user?.userName ?? '—'}
              </p>
            </div>
          </div>

          <Separator className="tw-my-2" />

          <div className="tw-flex tw-flex-col tw-gap-2 tw-px-3">
            <Button
              variant="ghost"
              size="lg"
              className="tw-h-12 tw-w-full tw-justify-start"
              onClick={() => {
                setProfileOpen(false);
                navigate('/field/profile');
              }}
            >
              <UserRound className="tw-mr-2 tw-h-4 tw-w-4" />
              My Profile
            </Button>
            <Button
              variant="outline"
              size="lg"
              className="tw-h-12 tw-w-full tw-justify-start"
              onClick={() => {
                setProfileOpen(false);
                navigate('/dashboard');
              }}
            >
              <Monitor className="tw-mr-2 tw-h-4 tw-w-4" />
              Desktop View
            </Button>
            <Button
              variant="ghost"
              size="lg"
              className="tw-h-12 tw-w-full tw-justify-start tw-text-destructive hover:tw-bg-destructive/10 hover:tw-text-destructive"
              onClick={() => {
                setProfileOpen(false);
                navigate('/logout');
              }}
            >
              <LogOut className="tw-mr-2 tw-h-4 tw-w-4" />
              Sign Out
            </Button>
          </div>
        </DrawerContent>
      </Drawer>

      <FieldBottomNav />
    </div>
  );
}
